import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Layers } from "lucide-react";
import { Modal } from "@telluscompany/design-system";

import { useLocalization, translateOperationalModule } from "@i18n";
import {
  useAccessibleStructuresQuery,
  getOperationalModuleSlug,
  type OperationalStructure,
} from "@shared/api/server/gs3Server";

const StructureOptionCard = ({
  structure,
  slug,
}: {
  structure: OperationalStructure;
  slug: string;
}) => (
  <Link
    to={`/${slug}/${structure.structureId}`}
    className={[
      "group flex h-full min-h-[152px] w-[184px] flex-col items-center justify-between gap-3 rounded-2xl border border-border/80 bg-background px-4 py-4 shadow-sm",
      "transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md",
      "focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
    ].join(" ")}
  >
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary/15">
      <Layers size={20} strokeWidth={2} aria-hidden />
    </div>

    <div className="flex w-full min-w-0 flex-1 flex-col items-center justify-center gap-1 text-center">
      <span className="line-clamp-2 text-sm font-semibold leading-snug text-foreground">
        {structure.structureName}
      </span>
      <span className="line-clamp-2 w-full text-xs leading-relaxed text-muted-foreground">
        {structure.complexName}
      </span>
    </div>

    <ChevronRight
      size={17}
      aria-hidden
      className="shrink-0 text-muted-foreground/45 transition-transform group-hover:translate-x-0.5 group-hover:text-primary"
    />
  </Link>
);

type OperationalStructureSelectorProps = {
  slug: string;
  structures: OperationalStructure[];
  isLoading: boolean;
};

export const OperationalStructureSelector = ({
  slug,
  structures,
  isLoading,
}: OperationalStructureSelectorProps) => {
  const { translate } = useLocalization();

  if (isLoading) {
    return (
      <div className="flex min-h-[120px] items-center justify-center py-4">
        <div
          role="status"
          aria-busy="true"
          className="h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/25 border-t-primary"
        />
      </div>
    );
  }

  if (structures.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        {translate("operationalModules.selector.empty")}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4 px-1">
      <p className="text-center text-sm text-muted-foreground">
        {translate("operationalModules.selector.title")}
      </p>
      <ul className="mx-auto grid w-fit max-w-full grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
        {structures.map((structure) => (
          <li key={structure.structureId} className="flex justify-center">
            <StructureOptionCard structure={structure} slug={slug} />
          </li>
        ))}
      </ul>
    </div>
  );
};

type OperationalModulePickerModalProps = {
  moduleKey: string;
  onClose: () => void;
};

export const OperationalModulePickerModal = ({
  moduleKey,
  onClose,
}: OperationalModulePickerModalProps) => {
  const { translate } = useLocalization();
  const slug = getOperationalModuleSlug(moduleKey) ?? "";

  const { data: structures = [], isLoading } =
    useAccessibleStructuresQuery(moduleKey);

  useEffect(() => {
    document.body.classList.add("operational-module-picker-modal-open");
    return () => {
      document.body.classList.remove("operational-module-picker-modal-open");
    };
  }, []);

  return (
    <Modal
      open
      onClose={onClose}
      title={translateOperationalModule(moduleKey, translate)}
      content={
        <OperationalStructureSelector
          slug={slug}
          structures={structures}
          isLoading={isLoading}
        />
      }
    />
  );
};
